<template>
  <div class="app-branded-dropdown">
    <div class="dropdown-wrapper" @scroll="grabScrollEvent">
      <div class="dropdown" ref="dropdown">
        <div class="clear-input" v-if="showClear" @click.prevent="clearSelection"></div>
        <div class="dropdown-border">
          <input
            :id="name"
            ref="input"
            class="form-control custom-select-only"
            :class="[open ? 'option-list-active' : '', showClear ? 'clearable' : '']"
            type="text"
            :name="name"
            spellcheck="false"
            :value="userInput"
            :placeholder="placeholder"
            @keyup.enter.prevent="onSelectByReturnKey();"
            @focus="onFocusSelectField()"
            @blur="onBlurSelectField()"
            @keydown="onArrowKeyScroll"
            @mousedown="onMousedownSelectField()"
            autocomplete="off"
            :disabled="inputDisabled"
            :readonly="true"
          >
        </div>
        <div class="option-list-wrapper"
             @mousemove="scrollHandlerOnDrag"
             @drag="scrollHandlerOnDrag"
             @mouseover="scrollHandlerOnDragCatch"
             @mouseup="scrollHandlerOnDragEnd"
             @mouseleave="scrollHandlerOnDragEnd"
             v-show="open">
          <ul ref="dropdownListContainer" tabindex="-1" class="option-list" @scroll="scrollHandlerOnScroll">
            <li class="no-item-found" v-if="noOptionsAvailable"><slot name="lblNoResults">-</slot></li>
            <li v-for="(option, index)  in options"
                :key="option.key"
                :class="{ focus: index === focusIndex }"
                @mouseover.prevent="listItemMouseOverAndMouseLeave(index)"
                @mouseleave.prevent="listItemMouseOverAndMouseLeave(index)"
                @mousedown="applySelection(option)"
            >
              <div class="item-wrapper">
                <div class="name">{{ option.value }}</div>
              </div>
            </li>
          </ul>
          <div ref="scrollHandle" class="scroll-handle" :class="{'invisible': !showScrollbar}" @mousedown="scrollHandlerOnDragStart"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import syncProperty from '@/mixins/syncProperty'

export default {
  name: 'BrandedDropdown',
  data () {
    return {
      userInput: '',
      open: false,
      focusIndex: 0,
      lastFocused: null,
      scrollHandlerDragging: false,
      scrollHandlerDragStartPos: 0,
      scrollHandlerDragLastPageY: 0,
      blockClickEvent: false
    }
  },
  mounted () {
    this.setInputToSelected()
  },
  watch: {
    selected_: function () {
      this.setInputToSelected()
    }
  },
  computed: {
    inputDisabled: function () {
      return this.disabled
    },
    showScrollbar: function () {
      if (!this.open) {
        return false
      }
      return true
    },
    noOptionsAvailable: function () {
      return this.options.length === 0
    },
    indexForSelection: function () {
      const comp = this
      if (comp.selected_ === null) {
        return 0
      }
      const index = this.options.findIndex((option) => option.key === comp.selected_.key)
      if (index < 0) return 0
      return index
    },
    showClear: function () {
      if (!this.clearable || !this.open) {
        return false
      }
      return this.userInput && this.userInput !== null && this.userInput !== ''
    }
  },
  methods: {
    setInputToSelected: function () {
      if (this.selected_ !== null) {
        this.userInput = this.selected_.value
      } else {
        this.userInput = ''
      }
    },
    onFocusSelectField () {
      this.open = true
      const inputref = this.$refs.input
      inputref.addEventListener('click', this.onClickSelectField)
    },
    onClickSelectField () {
      if (!this.blockClickEvent) {
        this.onBlurSelectField()
        this.$refs.input.blur()
      }
    },
    onMousedownSelectField () {
      if (!this.open) {
        this.blockClickEvent = true
      } else {
        this.blockClickEvent = false
      }
    },
    onBlurSelectField () {
      this.open = false
      this.$refs.input.removeEventListener('click', this.onClickSelectField)
    },
    applySelection (option) {
      this.selected_ = option
      this.onBlurSelectField()
      this.focusIndex = 0
      if (typeof this.tracking === 'function') {
        this.tracking(this.name, option?.key || '')
      }
    },
    listItemMouseOverAndMouseLeave (index) {
      if (!this.scrollHandlerDragging) {
        this.focusIndex = index
      }
    },
    scrollHandlerOnDragCatch (e) {
      if (this.scrollHandlerDragging) {
        e.preventDefault()
      }
    },
    scrollHandlerOnDrag (e) {
      e.preventDefault()
      if (this.scrollHandlerDragging) {
        const ch = this.$refs.dropdownListContainer.clientHeight
        const sh = this.$refs.dropdownListContainer.scrollHeight
        const maxSt = sh - ch
        const delta = e.pageY - this.scrollHandlerDragLastPageY
        this.scrollHandlerDragLastPageY = e.pageY
        let cp = (this.scrollHandlerDragStartPos += delta)
        // boundaries
        if (cp < 10) {
          cp = 10
        }
        if (cp > ch - 30) {
          cp = ch - 30
        }
        const relativePositionOfHandle = (cp - 10) / (ch - 40)
        const raf = window.requestAnimationFrame || window.setImmediate || function (c) { return setTimeout(c, 0) }
        // scrollTop must be between [0, maxSt] ~
        raf(() => {
          this.$refs.dropdownListContainer.scrollTop = maxSt * relativePositionOfHandle
          this.$refs.scrollHandle.style.top = cp + 'px'
        })
      }
    },
    scrollHandlerOnDragStart (e) {
      e.preventDefault()
      this.scrollHandlerDragging = true
      this.scrollHandlerDragStartPos = this.$refs.scrollHandle.offsetTop
      this.scrollHandlerDragLastPageY = e.pageY
    },
    scrollHandlerOnDragEnd (e) {
      this.scrollHandlerDragging = false
    },
    scrollHandlerOnScroll (e) {
      if (!this.scrollHandlerDragging && this.$refs.dropdownListContainer) {
        const ch = this.$refs.dropdownListContainer.clientHeight
        const st = this.$refs.dropdownListContainer.scrollTop
        const sh = this.$refs.dropdownListContainer.scrollHeight
        const sr = ch / sh
        const maxSt = sh - ch
        const raf = window.requestAnimationFrame || window.setImmediate || function (c) { return setTimeout(c, 0) }

        // scrollable
        if (sr < 1) {
          // calculate top position of scroll handler within list
          let ht = Math.floor(st / maxSt * ch)
          if (ht < 10) {
            ht = 10
          }
          if (ht > ch - 30) {
            ht = ch - 30
          }
          raf(() => {
            if (this.$refs.scrollHandle) {
              this.$refs.scrollHandle.style.display = 'block'
              this.$refs.scrollHandle.style.top = ht + 'px'
            }
          })
        } else {
          raf(() => {
            // nothing to scroll
            if (this.$refs.scrollHandle) {
              this.$refs.scrollHandle.style.display = 'none'
            }
          })
        }
      }
    },
    grabScrollEvent (e) {
      if (e.stopPropagation) {
        e.stopPropagation()
      }
    },
    clearSelection () {
      // only if we are in search mode
      if (this.open) {
        this.reset()
        this.$nextTick(() => {
          this.open = true
          this.$refs.input.focus()
        })
      }
    },
    reset () {
      this.selected_ = null
      this.userInput = null
    },
    // key handling
    onArrowKeyScroll (event) {
      let elHeight
      let y = 0
      const numEl = this.options.length
      let prevent = false
      let scrollTo = y

      if (document.querySelector('.focus')) {
        elHeight = this.$el.querySelector('.focus').offsetHeight
      } else {
        elHeight = 30
      }
      if (this.$el.querySelector('.option-list')) {
        y = this.$el.querySelector('.option-list').scrollTop
      }
      switch (event.keyCode) {
        // page up
        case 33:
          this.focusIndex = 0
          scrollTo = 0
          prevent = true
          break
        // arrow up
        case 38:
          if (this.focusIndex > 0) {
            this.focusIndex--
          }
          scrollTo = (this.focusIndex * elHeight)
          prevent = true
          break
        // page down
        case 34:
          this.focusIndex = numEl - 1
          scrollTo = (this.focusIndex * elHeight)
          prevent = true
          break
        // arrow down
        case 40:
          if (this.focusIndex < numEl - 1) {
            this.focusIndex++
          }
          scrollTo = (this.focusIndex * elHeight)
          prevent = true
          break
      }

      this.lastFocused = null
      if (this.focusIndex < this.options.length) {
        this.lastFocused = this.options[this.focusIndex]
      }

      // ensure valid range
      if (scrollTo < 0) {
        scrollTo = 0
      }

      if (scrollTo > (numEl - 1) * elHeight) {
        scrollTo = (numEl - 1) * elHeight
      }

      if (this.$el.querySelector('.option-list')) {
        this.$el.querySelector('.option-list').scrollTop = scrollTo
      }

      if (prevent) {
        event.preventDefault()
      }
    },
    onSelectByReturnKey () {
      this.applySelection(this.lastFocused)
      this.$refs.input.blur()
    }
  },
  mixins: [
    syncProperty('selected', 'selected_')

  ],
  props: {
    name: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Bitte wählen'
    },
    clearable: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    tracking: {
      type: Function,
      default: null
    }
  }
}
</script>
